import { useTraduccion } from '@tallerya/i18n';
import { IconoMenu, IconoPanelDerecho, useEsCelular } from '@tallerya/ui';
import { Sesion } from '../componentes/Sesion';
import { SelectorSucursal } from '../sucursal/SelectorSucursal';
import { SelectorTaller } from '../taller/SelectorTaller';
import { useTituloActual } from './titulo';

interface Lateral {
  /** El id del lateral que abre: el lector de pantalla anuncia qué controla. */
  id: string;
  visible: boolean;
  alternar: () => void;
}

interface Props {
  menu: Lateral;
  barra: Lateral;
}

/**
 * La barra superior del admin (#99): «AppBar» del Figma, 60 px sobre el guinda
 * del encabezado, con quién está adentro a la derecha.
 *
 * En celular suma lo que en escritorio sobra: los botones de los dos cajones y
 * el título de la pantalla, porque ahí el `<h1>` de la página queda lejos.
 * Fuera del celular no se dibujan, en vez de esconderse con CSS: el lector de
 * pantalla tampoco debe encontrar botones que no hacen nada.
 */
export function AppBar({ menu, barra }: Props) {
  const { t } = useTraduccion();
  const titulo = useTituloActual();
  const esCelular = useEsCelular();

  return (
    <header className="appbar">
      {esCelular && (
        <>
          <button
            type="button"
            className="appbar__boton"
            aria-label={t('admin.layout.menu')}
            aria-expanded={menu.visible}
            aria-controls={menu.id}
            onClick={menu.alternar}
          >
            <IconoMenu />
          </button>
          <p className="appbar__titulo">{titulo}</p>
        </>
      )}

      {/* El taller va antes que la sucursal porque la contiene: primero en qué
          taller, después en qué local de ese taller (#205). */}
      <div className="appbar__taller">
        <SelectorTaller />
      </div>

      {/* El lugar queda reservado aunque el selector todavía no tenga qué
          mostrar: así, cuando llegan las sucursales, lo demás no se mueve. */}
      <div className="appbar__sucursal">
        <SelectorSucursal />
      </div>

      <Sesion />

      {esCelular && (
        <button
          type="button"
          className="appbar__boton"
          aria-label={t('admin.layout.panel')}
          aria-expanded={barra.visible}
          aria-controls={barra.id}
          onClick={barra.alternar}
        >
          <IconoPanelDerecho />
        </button>
      )}
    </header>
  );
}
